/**
 * zLayers — the single documented z-index scale for chrome that must sit above
 * the map (BUG-462, TASK-4748).
 *
 * ## Why this file exists
 *
 * The app stacks two families of values that were never reconciled:
 *
 *  - **App chrome** (shell banners, sidebar, modals) used the plain Tailwind
 *    scale — `z-20`, `z-30`, `z-40`, `z-50`.
 *  - **Map overlays** used Leaflet's own convention — `z-[400]`..`z-[1100]`.
 *
 * Leaflet assigns its panes and controls z-indexes in the **400–1000 band**
 * (tile pane 200, overlay pane 400, shadow 500, marker 600, tooltip 650, popup
 * 700, and `.leaflet-control` 800–1000). Anything rendered as a sibling *inside*
 * the map container therefore has to bid inside that same band to be visible and
 * clickable — which is why `MapCanvas`'s corner slots are `z-[600]` and its
 * collapse toggle is `z-[1100]`. Those numbers are correct and must not be
 * lowered: they are relative to Leaflet's panes, not to the app shell.
 *
 * **The 400–1000 band is reserved for Leaflet and map-internal overlays. Never
 * assign app chrome a value inside it.** App chrome that must outrank the whole
 * map goes at {@link Z.chromeBanner} or above.
 *
 * ## The stacking-context subtlety that makes this safe
 *
 * A map overlay's `z-[600]` does **not** compete directly with a banner's `z-50`
 * whenever the map lives inside an ancestor that establishes its own stacking
 * context (a positioned element with a z-index of its own — e.g. the fullscreen
 * map overlay at {@link Z.mapFullscreen}, or `RouteDetailPage`'s `relative z-0`
 * wrapper). Inside such a context the child's 600 is clamped to its parent's
 * slot in the parent's context. So the number that actually decides whether the
 * banner wins is the **map container's** z-index, not the overlay's.
 *
 * That is exactly why BUG-462 could not be fixed by raising the banner: the
 * banner already outranked the map. See {@link Z.mapFullscreen}.
 *
 * ## Usage
 *
 * These are plain numbers, applied via the `style` prop or Tailwind arbitrary
 * values. Prefer the exported Tailwind class constants ({@link ZClass}) so the
 * value stays greppable and no component hardcodes a magic number.
 */

/**
 * The numeric z-index scale. Ordered low → high; every entry documents what it
 * must out-rank.
 */
export const Z = {
  /**
   * Fullscreen map overlay (Orders / Routes "map" view mode). It is
   * `position: fixed; inset: 0`, so it covers the viewport INCLUDING the shell's
   * banner strip.
   *
   * It sits **below** {@link Z.chromeBanner} deliberately: the shell banners
   * must remain visible and clickable on top of the map. Because this element
   * is positioned and carries a z-index, it establishes a stacking context that
   * contains every `z-[400]`..`z-[1100]` map overlay inside it — so the whole
   * map, controls included, resolves under the banners as one unit.
   */
  mapFullscreen: 40,

  /**
   * Shell chrome banners: the impersonation banner and its siblings
   * (maintenance, optimization / build-route / geocode progress).
   *
   * Must out-rank {@link Z.mapFullscreen} so the impersonation banner — a
   * safety-critical affordance telling a superadmin they are mutating another
   * tenant's data — is never painted over by the map.
   */
  chromeBanner: 50,

  /**
   * Modals, dialogs and their backdrops, plus portalled popovers that must
   * escape the map's Leaflet band entirely (`z-[9999]` dropdowns etc. predate
   * this scale and are left as-is).
   *
   * Above the banners: a dialog opened from a banner action must cover it.
   */
  modal: 60,
} as const;

export type ZLayer = keyof typeof Z;

/**
 * Tailwind class strings for {@link Z}. Using these keeps z-index out of inline
 * styles (DEV_RULES §"No inline styles") while staying greppable — searching
 * for `ZClass.chromeBanner` finds every element on the chrome layer.
 *
 * NOTE: these are written as complete literal class names so Tailwind's content
 * scanner can see them; never build them by interpolation.
 */
export const ZClass = {
  mapFullscreen: "z-40",
  chromeBanner: "z-50",
  modal: "z-[60]",
} as const satisfies Record<ZLayer, string>;

/**
 * Height of the shell chrome strip (impersonation + progress banners) exposed
 * to fixed-position descendants as a CSS custom property.
 *
 * ### Why a CSS variable rather than a bigger z-index
 *
 * BUG-462's real defect is **geometric**, not paint order. The fullscreen map
 * overlay is `fixed inset-0`, so it starts at viewport `top: 0` — underneath the
 * banner strip — and its top row of controls ("Показать все", the Leaflet zoom
 * control, the KPI capsule) is physically clipped by the banner painting over
 * them. Raising the banner's z-index changes nothing, because the banner is
 * already on top; the controls would stay hidden.
 *
 * The fix is to make the overlay *reserve* the strip. The shell publishes its
 * banner height into this variable and the overlay offsets its top edge by it,
 * so the map starts below the chrome instead of behind it.
 */
export const CHROME_OFFSET_VAR = "--chrome-offset";

/**
 * Tailwind class that offsets a `fixed inset-0` overlay below the shell chrome.
 *
 * Falls back to `0px` when the variable is unset (no banners active, or the
 * overlay is rendered outside a shell), so the map still fills the viewport in
 * the common non-impersonating case.
 */
export const CHROME_OFFSET_TOP_CLASS = "top-[var(--chrome-offset,0px)]";
